import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mic, Maximize2, Sparkles, ListPlus, Trash2, Play } from "lucide-react";
import { api, APP_ORIGIN } from "@/lib/api";
import VoiceTrackRecorder from "./VoiceTrackRecorder";

const STATUS_BADGE = {
  played: ["Played", "bg-emerald-400/15 text-emerald-300"],
  scheduled: ["Scheduled", "bg-sky-400/15 text-sky-300"],
  pending_pd_approval: ["Pending PD", "bg-amber-400/15 text-amber-300"],
  draft: ["Draft", "bg-white/10 text-ink-muted"],
  ready: ["Ready", "bg-brand-cyan/15 text-brand-cyan"],
  recorded: ["Recorded", "bg-violet-400/15 text-violet-300"],
};

export default function VoiceTrackPanel({ docked = true }) {
  const navigate = useNavigate();
  const [sample, setSample] = useState(null);
  const [tracks, setTracks] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [playingId, setPlayingId] = useState(null);

  const load = () => Promise.all([
    api.get("/production/hub/sample").then((r) => setSample(r.data)),
    api.get("/production/voicetracks").then((r) => setTracks(r.data.items || [])),
  ]).catch(() => {});
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const h = () => load();
    window.addEventListener("vt-library-refresh", h);
    return () => window.removeEventListener("vt-library-refresh", h);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = useMemo(() => (tracks || []).find((t) => t.vt_id === selectedId) || null, [tracks, selectedId]);

  const rows = useMemo(() => {
    if (tracks && tracks.length) {
      return tracks.slice(0, docked ? 5 : 30).map((t) => ({
        id: t.vt_id, title: t.title, dj: t.dj_name || "—",
        duration: t.duration ? `${Number(t.duration).toFixed(1)}s` : "—",
        type: t.category || "—",
        status: t.approval_status === "pending_pd_approval" && t.status === "draft" ? "pending_pd_approval" : t.status,
        hasAudio: !!t.has_audio, sample: false,
      }));
    }
    return (sample?.sample_voice_tracks || []).map((t, i) => ({
      id: `sample_${i}`, title: t.title, dj: t.dj, duration: t.duration, type: t.type, status: t.status, sample: true,
    }));
  }, [tracks, sample, docked]);

  if (!sample) return <div className="rounded-xl border border-line bg-bg-surface p-6 text-xs text-ink-muted">Loading VoiceTrack Studio…</div>;

  const insertIntoLog = async (row) => {
    try {
      await api.post("/production/on-air-log", {
        entry_type: "VT", title: row.title, artist: row.dj,
        duration: parseFloat(row.duration) || 0, vt_id: row.sample ? null : row.id,
      });
      toast.success(`"${row.title}" inserted into today's on-air log`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Insert failed"); }
  };
  const del = async (row) => {
    try { await api.delete(`/production/voicetracks/${row.id}`); if (selectedId === row.id) setSelectedId(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const playRow = (row) => {
    document.getElementById("vt-row-player")?.remove();
    const a = document.createElement("audio");
    a.id = "vt-row-player";
    a.src = `${APP_ORIGIN}/api/production/voicetracks/${row.id}/audio`;
    a.play().then(() => setPlayingId(row.id)).catch(() => toast.error("No recording on this voice track yet"));
    a.onended = () => setPlayingId(null);
    document.body.appendChild(a);
  };

  return (
    <div className="rounded-xl border border-line bg-bg-surface" data-testid="voicetrack-panel">
      <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
        <div className="flex items-center gap-2 text-sm font-bold"><Mic className="h-4 w-4 text-brand-amber" /> VoiceTrack Studio</div>
        {docked && (
          <button onClick={() => navigate("/dashboard/production/voicetrack")} data-testid="vt-open-full"
            title="Open Full Studio" className="rounded border border-line p-1 text-ink-muted hover:text-ink-primary">
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <div className="flex items-center gap-2.5">
            <div className="h-11 w-11 rounded-md shrink-0" style={{ background: "linear-gradient(135deg,#6366f1,#a855f7)" }} />
            <div className="min-w-0">
              <div className="font-mono text-[8px] uppercase tracking-wider text-ink-muted">Previous Song <span className="rounded bg-white/10 px-1">SAMPLE</span></div>
              <div className="text-sm font-bold truncate">{sample.previous_song.title}</div>
              <div className="text-[10px] text-ink-muted truncate">{sample.previous_song.artist} · {sample.previous_song.duration} · outro in {sample.previous_song.outro_in}</div>
            </div>
          </div>
          <div className="text-center">
            <div className="font-mono text-xl font-black text-brand-amber">{sample.previous_song.outro_in}</div>
            <div className="font-mono text-[8px] uppercase tracking-wider text-ink-muted">On air in (auto-fade)</div>
          </div>
          <div className="flex items-center gap-2.5 justify-end text-right">
            <div className="min-w-0">
              <div className="font-mono text-[8px] uppercase tracking-wider text-ink-muted">Next Song <span className="rounded bg-white/10 px-1">SAMPLE</span></div>
              <div className="text-sm font-bold truncate">{sample.next_song.title}</div>
              <div className="text-[10px] text-ink-muted truncate">{sample.next_song.artist} · {sample.next_song.duration} · vocal starts {sample.next_song.vocal_starts}</div>
            </div>
            <div className="h-11 w-11 rounded-md shrink-0" style={{ background: "linear-gradient(135deg,#fb923c,#f43f5e)" }} />
          </div>
        </div>

        <VoiceTrackRecorder vt={selected} onSaved={(vtId) => { load(); setSelectedId(vtId); }} />

        <div className="flex flex-wrap gap-2">
          <button onClick={() => toast.info("AI Voice Break scripts route through Show Prep in Phase 3 — record or upload your break above for now.")} data-testid="vt-generate-break"
            className="inline-flex items-center gap-1.5 rounded-md border border-line px-4 py-2 text-xs font-bold text-ink-secondary"><Sparkles className="h-3.5 w-3.5" /> Generate Voice Break</button>
        </div>

        <div data-testid="vt-library">
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted mb-2">
            Voice Track Library {selected ? <span className="text-brand-amber">· click a row to select for the editor</span> : "· click a row to edit"}
          </div>
          <div className="grid grid-cols-[2fr_1fr_1fr_1fr_auto_auto] gap-2 font-mono text-[8px] uppercase tracking-wider text-ink-muted border-b border-line pb-1">
            <span>Title</span><span>DJ</span><span>Duration</span><span>Type</span><span>Status</span><span></span>
          </div>
          {rows.map((r) => {
            const [label, cls] = STATUS_BADGE[r.status] || STATUS_BADGE.draft;
            const sel = selectedId === r.id;
            return (
              <div key={r.id} onClick={() => !r.sample && setSelectedId(sel ? null : r.id)}
                className={`grid grid-cols-[2fr_1fr_1fr_1fr_auto_auto] gap-2 items-center py-2 border-b border-line/40 last:border-0 text-xs cursor-pointer transition ${sel ? "bg-brand-amber/5" : "hover:bg-white/[0.02]"}`}
                data-testid={`vt-row-${r.id}`}>
                <span className="font-semibold truncate">{sel && <span className="text-brand-amber mr-1">▸</span>}{r.title}{r.sample && <span className="ml-1.5 rounded bg-white/10 px-1 py-px font-mono text-[8px] text-ink-muted">SAMPLE</span>}</span>
                <span className="text-ink-muted truncate">{r.dj}</span>
                <span className="font-mono text-ink-muted">{r.duration}</span>
                <span className="text-ink-muted truncate">{r.type}</span>
                <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold ${cls}`}>{label}</span>
                <span className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                  {r.hasAudio && (
                    <button onClick={() => playRow(r)} title="Play recording" data-testid={`vt-play-${r.id}`}
                      className={`rounded border border-line p-1 ${playingId === r.id ? "text-brand-amber" : "text-ink-muted hover:text-brand-amber"}`}><Play className="h-3 w-3" /></button>
                  )}
                  <button onClick={() => insertIntoLog(r)} title="Insert Into Log" data-testid={`vt-insert-${r.id}`}
                    className="rounded border border-line p-1 text-ink-muted hover:text-brand-amber"><ListPlus className="h-3 w-3" /></button>
                  {!r.sample && (
                    <button onClick={() => del(r)} title="Delete" data-testid={`vt-del-${r.id}`}
                      className="rounded border border-line p-1 text-ink-muted hover:text-rose-300"><Trash2 className="h-3 w-3" /></button>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
